import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskCompletion, TaskHoliday, TaskSchedule } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { useAuth } from '../../context/AuthContext';
import { taskApi, taskErrorMessage } from '../../utils/taskApi';
import { expandOccurrences, recurrenceLabel } from '../../utils/recurrence';
import {
  addDays,
  addMonths,
  daysBetween,
  longDate,
  monthTitle,
  mondayOf,
  parseDay,
  shortDate,
  timeOfTimestamp,
  weekdayShort,
} from '../../utils/taskDates';
import { firstName, userName } from '../../utils/taskUsers';
import TaskIcon, { TaskIconName } from './TaskIcon';
import SchedulesDialog from './SchedulesDialog';
import { BubbleStack } from './UserBubble';
import { fillPlaceholders } from '../../utils/placeholders';
import { itemUrl } from '../../utils/taskLinks';
import '../../tasks.css';

type View = 'month' | 'week' | 'day';
type Kind = 'event' | 'milestone' | 'scheduled' | 'recurring' | 'due' | 'overdue' | 'follow_up' | 'done';

interface CalEvent {
  key: string;
  date: string;
  kind: Kind;
  title: string;
  people: string[];
  detail?: string;
  added?: boolean;
  schedule?: TaskSchedule;
  itemId?: number | null;
}

const KIND_META: Record<Kind, { label: string; icon: TaskIconName }> = {
  event: { label: 'Event', icon: 'event' },
  milestone: { label: 'Milestone', icon: 'milestone' },
  scheduled: { label: 'Scheduled', icon: 'scheduled' },
  recurring: { label: 'Recurring', icon: 'recurring' },
  due: { label: 'Due', icon: 'due' },
  overdue: { label: 'Overdue', icon: 'due' },
  follow_up: { label: 'Follow-up', icon: 'waiting' },
  done: { label: 'Completed', icon: 'check' },
};
const KIND_ORDER: Kind[] = ['event', 'milestone', 'overdue', 'due', 'follow_up', 'scheduled', 'recurring', 'done'];
const WEEK_HEAD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Month cells fill the window, so how many entries fit depends on its height.
const CHIP_HEIGHT = 22; // 20px chip + 2px gap
const CELL_CHROME = 36; // day number and padding
const MIN_CHIPS = 2;

function readPref<T extends string>(key: string, allowed: T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* per-viewer convenience only */
  }
}

const isPhone = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 640px)').matches;

const CalendarPage: React.FC = () => {
  const { ready, error, today, me, users, items, lists, rev, openComposer } = useTasks();
  // Only admins see the whole team's calendar; for everyone else the server
  // already narrows it to their own work, so there's no Everyone/Mine choice.
  const isAdmin = useAuth().permissions?.user_type === 'Admin';
  const navigate = useNavigate();
  const [view, setViewState] = useState<View>(() => readPref('calendar.view', ['month', 'week', 'day'], 'month'));
  const [who, setWhoState] = useState<'all' | 'mine'>(() => readPref('calendar.who', ['all', 'mine'], 'all'));
  const [cursor, setCursor] = useState(today);
  const [selected, setSelected] = useState(today);
  const [data, setData] = useState<{ completions: TaskCompletion[]; schedules: TaskSchedule[]; holidays?: TaskHoliday[] } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showSchedules, setShowSchedules] = useState(false);
  const monthRef = useRef<HTMLDivElement>(null);
  const [monthChips, setMonthChips] = useState(3);

  // Fit as many entries in each month cell as its height allows, leaving a
  // line for "+N more".
  useEffect(() => {
    const grid = monthRef.current;
    if (!grid || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const cell = grid.querySelector('.tc-cell') as HTMLElement | null;
      if (!cell) return;
      const fit = Math.floor((cell.clientHeight - CELL_CHROME) / CHIP_HEIGHT) - 1;
      setMonthChips(Math.max(MIN_CHIPS, fit));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [view, ready]);

  const setView = (v: View) => {
    setViewState(v);
    writePref('calendar.view', v);
  };
  const setWho = (v: 'all' | 'mine') => {
    setWhoState(v);
    writePref('calendar.who', v);
  };

  const range = useMemo(() => {
    if (view === 'day') return { from: cursor, to: cursor };
    if (view === 'week') {
      const from = mondayOf(cursor);
      return { from, to: addDays(from, 6) };
    }
    const first = `${cursor.slice(0, 7)}-01`;
    const from = mondayOf(first);
    return { from, to: addDays(from, 41) };
  }, [view, cursor]);

  const load = useCallback(async () => {
    try {
      setData(await taskApi.calendar(range.from, range.to));
      setLoadError(null);
    } catch (e) {
      setLoadError(taskErrorMessage(e, 'Could not load the calendar'));
    }
  }, [range.from, range.to]);

  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  // Ticks and schedule edits change what the calendar shows.
  useEffect(() => {
    if (!ready) return;
    const timer = setTimeout(load, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rev]);
  useEffect(() => {
    window.addEventListener('tasks:schedules-changed', load);
    return () => window.removeEventListener('tasks:schedules-changed', load);
  }, [load]);

  const listTitle = useMemo(() => new Map(lists.map((l) => [l.id, l.title])), [lists]);

  // Closed days: holidays from Admin › Holidays, plus the Dec 24 – Jan 2 break.
  const holidayNames = useMemo(() => new Map((data?.holidays || []).map((h) => [h.day, h.name])), [data]);
  const closedLabel = (date: string): string | null => {
    const md = date.slice(5);
    return holidayNames.get(date) || (md >= '12-24' || md <= '01-02' ? 'Holiday break' : null);
  };

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalEvent[]>();
    const push = (e: CalEvent) => {
      if (e.date < range.from || e.date > range.to) return;
      map.set(e.date, [...(map.get(e.date) || []), e]);
    };
    const mine = isAdmin && who === 'mine';

    for (const s of data?.schedules || []) {
      const owners = s.assignees.length ? s.assignees : [s.created_by];
      if (mine && !owners.includes(me)) continue;
      if (!s.recurrence) {
        push({
          key: `s${s.id}`,
          date: s.start_date,
          kind: 'scheduled',
          title: fillPlaceholders(s.title, s.start_date),
          people: s.assignees,
          added: !!s.last_run_on,
          detail: s.target === 'new_list' ? 'New list' : "Weekly list",
          schedule: s,
        });
        continue;
      }
      if (!s.active) continue;
      // Upcoming occurrences only — past ones already became list items.
      const from = range.from > today ? range.from : today;
      for (const date of expandOccurrences(s.recurrence, from, range.to, s.start_date)) {
        push({
          key: `r${s.id}-${date}`,
          date,
          kind: 'recurring',
          title: fillPlaceholders(s.title, date),
          people: s.assignees,
          added: !!s.last_run_on && date <= s.last_run_on,
          detail: recurrenceLabel(s.recurrence) || undefined,
          schedule: s,
        });
      }
    }

    const itemIds = new Set(items.map((i) => i.id));
    for (const item of items) {
      if (item.progress >= 100) continue;
      // A week-list link shares its original's dates; show them once.
      if (item.linked_to && itemIds.has(item.linked_to)) continue;
      // A project's events (test day, race weekend) and milestones are for
      // everyone on the project, whoever's filter is on.
      if (item.node_kind === 'event' && item.plan_start && item.plan_finish) {
        const length = Math.min(31, daysBetween(item.plan_start, item.plan_finish) + 1);
        for (let i = 0; i < length; i++) {
          push({
            key: `e${item.id}-${i}`,
            date: addDays(item.plan_start, i),
            kind: 'event',
            title: item.text || 'Event',
            people: item.assignees,
            detail: listTitle.get(item.list_id),
            itemId: item.id,
          });
        }
        continue;
      }
      if (item.node_kind === 'milestone' && item.plan_finish) {
        push({
          key: `m${item.id}`,
          date: item.plan_finish,
          kind: 'milestone',
          title: item.text || 'Milestone',
          people: item.assignees,
          detail: listTitle.get(item.list_id),
          itemId: item.id,
        });
        continue; // its date is its due date — once is enough
      }
      if (mine && !item.assignees.includes(me)) continue;
      if (item.due_date) {
        push({
          key: `d${item.id}`,
          date: item.due_date,
          kind: item.due_date < today ? 'overdue' : 'due',
          title: item.text || 'Untitled',
          people: item.assignees,
          detail: listTitle.get(item.list_id),
          itemId: item.id,
        });
      }
      if (item.waiting_on && item.follow_up_date) {
        push({
          key: `f${item.id}`,
          date: item.follow_up_date,
          kind: 'follow_up',
          title: `${item.text || 'Untitled'} (${item.waiting_on})`,
          people: item.assignees.length ? item.assignees : [item.created_by],
          detail: listTitle.get(item.list_id),
          itemId: item.id,
        });
      }
    }

    for (const c of data?.completions || []) {
      // "Mine" includes work you handed out that someone has now finished.
      if (mine && c.completed_by !== me && c.assigned_by !== me) continue;
      push({
        key: `c${c.id}`,
        date: c.completed_on,
        kind: 'done',
        title: c.item_text,
        people: c.completed_by === 'system' ? [] : [c.completed_by],
        detail: `${c.completed_by === 'system' ? 'Resolved automatically' : firstName(users, c.completed_by)} · ${timeOfTimestamp(c.completed_at)}${c.list_title ? ` · ${c.list_title}` : ''}${c.assigned_by === me && c.completed_by !== me ? ' · assigned by you' : ''}`,
        itemId: c.item_id,
      });
    }

    map.forEach((list) => list.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)));
    return map;
  }, [data, items, who, isAdmin, me, range, today, listTitle, users]);

  const openEvent = (e: CalEvent) => {
    if (e.schedule) openComposer({ schedule: e.schedule });
    else if (e.itemId) {
      const item = items.find((i) => i.id === e.itemId);
      navigate(item ? itemUrl(item, lists) : `/lists?item=${e.itemId}`);
    }
  };

  const step = (dir: number) => {
    if (view === 'month') setCursor(addMonths(cursor, dir));
    else if (view === 'week') setCursor(addDays(cursor, dir * 7));
    else setCursor(addDays(cursor, dir));
  };

  const title =
    view === 'month'
      ? monthTitle(cursor)
      : view === 'week'
        ? `${shortDate(range.from)} – ${shortDate(range.to)}, ${parseDay(range.to).getFullYear()}`
        : longDate(cursor);

  const addOn = (date: string) => openComposer({ date, when: date > today ? 'date' : 'now' });

  if (!ready) {
    return (
      <div className="tk-page">
        <div className="tk-setup">
          <h2>Calendar</h2>
          <p>{error || 'Loading…'}</p>
        </div>
      </div>
    );
  }

  const chip = (e: CalEvent, compact = false) => (
    <button
      key={e.key}
      type="button"
      className={`tc-chip tc-${e.kind}${e.added ? ' is-added' : ''}`}
      onClick={(ev) => {
        ev.stopPropagation();
        openEvent(e);
      }}
      title={`${KIND_META[e.kind].label}: ${e.title}${e.people.length ? ` · ${e.people.map((p) => userName(users, p)).join(', ')}` : ''}${e.detail ? ` · ${e.detail}` : ''}`}
    >
      <TaskIcon name={KIND_META[e.kind].icon} size={11} />
      <span className="tc-chip-text">{e.title}</span>
      {!compact && e.people.length > 0 && <BubbleStack users={users} emails={e.people} max={2} />}
    </button>
  );

  const agenda = (date: string) => {
    const events = eventsByDay.get(date) || [];
    if (!events.length) {
      return (
        <div className="tc-agenda-empty">
          <p>Nothing on {date === today ? 'today' : longDate(date)}.</p>
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => addOn(date)}>
            <TaskIcon name="plus" size={14} /> Add a task
          </button>
        </div>
      );
    }
    return (
      <div className="tc-agenda">
        {KIND_ORDER.filter((k) => events.some((e) => e.kind === k)).map((kind) => (
          <section key={kind} className="tc-agenda-group">
            <h3 className={`tc-agenda-head tc-head-${kind}`}>
              <TaskIcon name={KIND_META[kind].icon} size={13} /> {KIND_META[kind].label}
            </h3>
            <ul>
              {events.filter((e) => e.kind === kind).map((e) => (
                <li key={e.key}>
                  <button type="button" className={`tc-agenda-row tc-${e.kind}${e.added ? ' is-added' : ''}`} onClick={() => openEvent(e)}>
                    <span className="tc-agenda-dot" />
                    <span className="tc-agenda-main">
                      <span className="tc-agenda-title">{e.title}</span>
                      {(e.detail || e.added) && (
                        <span className="tc-agenda-detail">
                          {[e.detail, e.added ? 'already on the list' : ''].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    {e.people.length > 0 && <BubbleStack users={users} emails={e.people} />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  };

  const days = (from: string, count: number) => Array.from({ length: count }, (_, i) => addDays(from, i));
  const currentMonth = cursor.slice(0, 7);

  return (
    <div className="tk-page tc-page">
      <header className="tc-toolbar">
        <div className="tc-nav">
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => { setCursor(today); setSelected(today); }}>Today</button>
          <button type="button" className="tk-icon-btn" onClick={() => step(-1)} aria-label="Previous"><TaskIcon name="chevron-left" /></button>
          <button type="button" className="tk-icon-btn" onClick={() => step(1)} aria-label="Next"><TaskIcon name="chevron-right" /></button>
          <h1 className="tc-title">{title}</h1>
        </div>
        <div className="tc-controls">
          {isAdmin && (
            <div className="tk-segmented" role="group" aria-label="Whose tasks">
              <button type="button" className={who === 'all' ? 'is-active' : ''} onClick={() => setWho('all')}>Everyone</button>
              <button type="button" className={who === 'mine' ? 'is-active' : ''} onClick={() => setWho('mine')}>Mine</button>
            </div>
          )}
          <div className="tk-segmented" role="group" aria-label="View">
            {(['day', 'week', 'month'] as View[]).map((v) => (
              <button key={v} type="button" className={view === v ? 'is-active' : ''} onClick={() => setView(v)}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setShowSchedules(true)} title="Scheduled & recurring tasks, including paused ones">
            <TaskIcon name="recurring" size={15} /> <span className="tk-hide-sm">Scheduled</span>
          </button>
          <button type="button" className="tk-btn tk-btn-primary" onClick={() => openComposer({ when: 'date', date: selected > today ? selected : addDays(today, 1) })}>
            <TaskIcon name="plus" size={15} /> <span className="tk-hide-sm">Schedule</span>
          </button>
        </div>
      </header>

      <div className="tc-legend" aria-hidden="true">
        {(['event', 'milestone', 'scheduled', 'recurring', 'due', 'follow_up', 'done'] as Kind[]).map((k) => (
          <span key={k} className={`tc-legend-item tc-${k}`}><span className="tc-dot" />{KIND_META[k].label}</span>
        ))}
      </div>

      {loadError && <div className="tk-error-bar">{loadError}</div>}

      {view === 'month' && (
        <>
          <div ref={monthRef} className="tc-month" role="grid" aria-label={monthTitle(cursor)}>
            {WEEK_HEAD.map((d) => (
              <div key={d} className="tc-weekday" role="columnheader">{d}</div>
            ))}
            {days(range.from, 42).map((date) => {
              const events = eventsByDay.get(date) || [];
              const outside = date.slice(0, 7) !== currentMonth;
              return (
                <div
                  key={date}
                  role="gridcell"
                  className={`tc-cell${outside ? ' is-outside' : ''}${date === today ? ' is-today' : ''}${date === selected ? ' is-selected' : ''}${closedLabel(date) ? ' is-closed' : ''}`}
                  onClick={() => {
                    setSelected(date);
                    if (!isPhone()) addOn(date);
                  }}
                >
                  <button
                    type="button"
                    className="tc-daynum"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (isPhone()) setSelected(date);
                      else {
                        setCursor(date);
                        setView('day');
                      }
                    }}
                    aria-label={longDate(date)}
                  >
                    {parseDay(date).getDate()}
                  </button>
                  {closedLabel(date) && <div className="tc-closed" title={`Closed — ${closedLabel(date)}`}>{closedLabel(date)}</div>}
                  <div className="tc-cell-chips">
                    {events.slice(0, events.length > monthChips + 1 ? monthChips : monthChips + 1).map((e) => chip(e, true))}
                    {events.length > monthChips + 1 && (
                      <button
                        type="button"
                        className="tc-more"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setCursor(date);
                          setView('day');
                        }}
                      >
                        +{events.length - monthChips} more
                      </button>
                    )}
                  </div>
                  {events.length > 0 && (
                    <div className="tc-dots" aria-hidden="true">
                      {Array.from(new Set(events.map((e) => e.kind))).slice(0, 4).map((k) => <span key={k} className={`tc-dot tc-${k}`} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <section className="tc-selected">
            <h2 className="tk-section-label">{selected === today ? 'Today' : longDate(selected)}</h2>
            {agenda(selected)}
          </section>
        </>
      )}

      {view === 'week' && (
        <div className="tc-week">
          {days(range.from, 7).map((date) => {
            const events = eventsByDay.get(date) || [];
            return (
              <div key={date} className={`tc-week-col${date === today ? ' is-today' : ''}${closedLabel(date) ? ' is-closed' : ''}`}>
                <button type="button" className="tc-week-head" onClick={() => { setCursor(date); setView('day'); }}>
                  <span className="tc-week-dow">{weekdayShort(date)}</span>
                  <span className="tc-week-num">{parseDay(date).getDate()}</span>
                  {closedLabel(date) && <span className="tc-closed">{closedLabel(date)}</span>}
                </button>
                <div className="tc-week-body">
                  {events.map((e) => chip(e))}
                  <button type="button" className="tc-week-add" onClick={() => addOn(date)} aria-label={`Add a task on ${longDate(date)}`}>
                    <TaskIcon name="plus" size={13} /> Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === 'day' && (
        <div className="tc-day">
          {closedLabel(cursor) && (
            <p className="tc-closed-banner">
              <TaskIcon name="calendar" size={14} /> Closed — {closedLabel(cursor)}
            </p>
          )}
          {agenda(cursor)}
          {(eventsByDay.get(cursor) || []).length > 0 && (
            <button type="button" className="tk-btn tk-btn-ghost tc-day-add" onClick={() => addOn(cursor)}>
              <TaskIcon name="plus" size={14} /> Add a task on {cursor === today ? 'today' : shortDate(cursor)}
            </button>
          )}
        </div>
      )}
      {showSchedules && <SchedulesDialog onClose={() => setShowSchedules(false)} />}
    </div>
  );
};

export default CalendarPage;
